import { useCallback, useEffect, useRef } from "react";

type DebouncedCallback<T extends (...args: never[]) => void> = T & {
  cancel: () => void;
};

/**
 * Returns a debounced version of the given callback.
 * The callback is delayed until `delay` ms after the last invocation.
 * Uses a callback ref so the latest closure is always called.
 */
export function useDebouncedCallback<T extends (...args: never[]) => void>(
  callback: T,
  delay: number,
): DebouncedCallback<T> {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  const debounced = useCallback(
    (...args: Parameters<T>) => {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => callbackRef.current(...args), delay);
    },
    [delay],
  ) as DebouncedCallback<T>;

  debounced.cancel = () => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = undefined;
  };

  return debounced;
}
